/**
 * ProcessingView
 *
 * Bucket #2 drill-down: payment processing.
 * Shows channel-by-channel breakdown of Beu-processed volume and margin.
 */

import React, { Component } from 'react';
import { adminService } from '../../../_services/admin.service';

const fmtGBP = (n) => (n == null || isNaN(n)) ? '—' : '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-GB');
const pct = (n) => (n == null || isNaN(n)) ? '—' : `${(Number(n) * 100).toFixed(2)}%`;

class ProcessingView extends Component {
    state = { loading: true, error: null, data: null };

    componentDidMount() { this.fetch(); }
    componentDidUpdate(prevProps) {
        if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
            this.fetch();
        }
    }

    fetch = async () => {
        this.setState({ loading: true, error: null });
        try {
            const { startDate, endDate } = this.props;
            const res = await adminService.getFinancialsProcessing({ startDate, endDate });
            if (res.statusCode === 1) {
                this.setState({ data: res.responseData, loading: false });
            } else {
                this.setState({ error: res.message || 'Failed to load processing data', loading: false });
            }
        } catch (err) {
            console.error('financials processing error', err);
            this.setState({ error: String(err), loading: false });
        }
    };

    render() {
        const { loading, error, data } = this.state;
        if (loading) return <div className="fin-loading">Loading processing data…</div>;
        if (error) return <div className="fin-error">{error}</div>;
        if (!data) return null;

        const tiles = [
            { label: 'On-platform volume', value: fmtGBP(data.totals?.volume), sub: `${fmtInt(data.totals?.txns)} transactions` },
            { label: 'Beu fee earned', value: fmtGBP(data.totals?.beuFee), sub: 'Gross (before Stripe cost)' },
            { label: 'Stripe fee (est)', value: fmtGBP(data.totals?.stripeFee), sub: `Estimated at ${data.tier?.name || 'current tier'} rate` },
            { label: 'Net margin', value: fmtGBP((data.totals?.beuFee || 0) - (data.totals?.stripeFee || 0)), sub: `Take-rate ${pct((data.totals?.beuFee || 0) / (data.totals?.volume || 1))}` },
            { label: 'Current Stripe tier', value: data.tier?.name || '—', sub: data.tier?.nextTier ? `Next tier at ${fmtGBP(data.tier.volumeToNext)} more` : 'Top tier reached' }
        ];

        return (
            <div>
                <div className="fin-kpi-strip">
                    {tiles.map(t => (
                        <div key={t.label} className="fin-kpi-tile">
                            <div className="fin-kpi-tile-label">{t.label}</div>
                            <div className="fin-kpi-tile-value">{t.value}</div>
                            {t.sub && <div className="fin-kpi-tile-sub">{t.sub}</div>}
                        </div>
                    ))}
                </div>

                {(data.settled || []).length > 0 && (
                    <div className="fin-section">
                        <div className="fin-section-header">
                            <h3 className="fin-section-title">Stripe-settled volume (MTD)</h3>
                            <span className="fin-section-sub">Matches Stripe dashboard · by pgObjectType</span>
                        </div>
                        <table className="fin-table">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th className="num">Volume</th>
                                    <th className="num">Txns</th>
                                    <th className="num">Avg txn</th>
                                    <th className="num">Beu fee</th>
                                    <th className="num">Stripe fee (est)</th>
                                    <th className="num">Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.settled.map(r => {
                                    const avg = r.txns ? r.volume / r.txns : 0;
                                    return (
                                        <tr key={r.pgObjectType}>
                                            <td>{r.channel}</td>
                                            <td className="num">{fmtGBP(r.volume)}</td>
                                            <td className="num">{fmtInt(r.txns)}</td>
                                            <td className="num">{fmtGBP(avg)}</td>
                                            <td className="num">{fmtGBP(r.beuFee)}</td>
                                            <td className="num">{fmtGBP(r.stripeFee)}</td>
                                            <td className="num">{fmtGBP(r.net)}</td>
                                        </tr>
                                    );
                                })}
                                <tr className="total">
                                    <td>Total</td>
                                    <td className="num">{fmtGBP(data.totals?.volume)}</td>
                                    <td className="num">{fmtInt(data.totals?.txns)}</td>
                                    <td className="num">—</td>
                                    <td className="num">{fmtGBP(data.totals?.beuFee)}</td>
                                    <td className="num">{fmtGBP(data.totals?.stripeFee)}</td>
                                    <td className="num">{fmtGBP((data.totals?.beuFee || 0) - (data.totals?.stripeFee || 0))}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="fin-section">
                    <div className="fin-section-header">
                        <h3 className="fin-section-title">By payment type</h3>
                        <span className="fin-section-sub">From bookings — paymentType breakdown · Stripe fee estimated from current tier</span>
                    </div>
                    <table className="fin-table">
                        <thead>
                            <tr>
                                <th>Channel</th>
                                <th className="num">Volume</th>
                                <th className="num">Txns</th>
                                <th className="num">Avg txn</th>
                                <th className="num">Beu fee</th>
                                <th className="num">Stripe fee (est)</th>
                                <th className="num">Net</th>
                                <th className="num">Take-rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.byChannel || []).map(ch => {
                                const avg = ch.txns ? ch.volume / ch.txns : 0;
                                const net = (ch.beuFee || 0) - (ch.stripeFee || 0);
                                const tr = ch.volume ? ch.beuFee / ch.volume : 0;
                                return (
                                    <tr key={ch.channel}>
                                        <td>{ch.channel}</td>
                                        <td className="num">{fmtGBP(ch.volume)}</td>
                                        <td className="num">{fmtInt(ch.txns)}</td>
                                        <td className="num">{fmtGBP(avg)}</td>
                                        <td className="num">{fmtGBP(ch.beuFee)}</td>
                                        <td className="num">{fmtGBP(ch.stripeFee)}</td>
                                        <td className="num">{fmtGBP(net)}</td>
                                        <td className="num">{pct(tr)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="fin-section">
                    <div className="fin-section-header">
                        <h3 className="fin-section-title">Stripe tier trajectory</h3>
                    </div>
                    <table className="fin-table">
                        <thead>
                            <tr>
                                <th>Tier</th>
                                <th className="num">Monthly volume</th>
                                <th className="num">% fee</th>
                                <th className="num">Fixed fee</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.tierLadder || []).map(t => (
                                <tr key={t.name}>
                                    <td>{t.name}</td>
                                    <td className="num">{t.range}</td>
                                    <td className="num">{t.percent}</td>
                                    <td className="num">{t.fixed}</td>
                                    <td>{t.current ? <span className="fin-pill ok">Current</span> : <span className="fin-dim">—</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export default ProcessingView;
